import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tabs, Tab } from '@heroui/react';
import { PaperPlaneTilt, ArrowBendUpLeft, X, ArrowLeft, Warning } from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import { ResourceSelector } from './ResourceSelector';
import { ContactsList } from './ContactsList';
import { TemplateManager } from './TemplateManager';
import { EmailComposer } from './EmailComposer';
import { ParameterManager } from './ParameterManager';
import { AttachmentManager } from './AttachmentManager';
import { EmailPreview } from './EmailPreview';
import { SendingProgress } from './SendingProgress';
import { DynamicFieldsManager } from './DynamicFieldsManager';
import { EntitiesManager } from './EntitiesManager';
import { SendResultsModal } from './SendResultsModal';
import { EmailHistorySection } from './EmailHistorySection';
import { useMassMailData } from '../hooks/useMassMailData';
import { useTemplateValidation } from '../hooks/useTemplateValidation';
import { useInvoiceAttachmentPrefetch } from '../hooks/useInvoiceAttachmentPrefetch';

import { CustomSendFunction, HelpdeskEmail } from '../types';
import { getSendBlockReason } from '../utils/sendBlockReason';
import { stripCidReferences, stripYallyTemplateWrapper } from '../utils/emailThreadParser';
import { QUOTED_REPLY_MARKER } from '../utils/translationUtils';

interface MassMailContentProps {
  isModal?: boolean;
  onModalClose?: () => void;
  onSuccess?: () => void;
  customSendFunction?: CustomSendFunction;
  emailHistory?: HelpdeskEmail[];
  replyToEmail?: HelpdeskEmail;
  /**
   * Sluit de composer meteen na een geslaagde verzending, zonder het interne
   * resultatenvenster. De aanroeper toont dan zelf het verzendrapport (zie
   * reno-tickets). Standaard blijft het bestaande gedrag: rapport in de composer,
   * die sluit wanneer de gebruiker het rapport sluit.
   */
  closeOnSuccess?: boolean;
}

export const MassMailContent: React.FC<MassMailContentProps> = ({
  isModal = false,
  onModalClose,
  onSuccess,
  customSendFunction,
  emailHistory,
  replyToEmail,
  closeOnSuccess = false
}) => {
  const navigate = useNavigate();
  const [leftTab, setLeftTab] = useState(emailHistory && emailHistory.length > 0 ? 'email-verloop' : 'contactpersonen');
  const [rightTab, setRightTab] = useState('editor');
  const [replyingToEmail, setReplyingToEmail] = useState<HelpdeskEmail | null>(null);
  const { state, dispatch } = useMassMailContext();

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  const { 
    fetchTemplateTypes, 
    fetchTemplates, 
    fetchEmailSettings,
    sendMassEmail
  } = useMassMailData(customSendFunction, { skipResultsModal: closeOnSuccess });
  const { isValid: isTemplateValid, getMissingEntityTypes, hasUnfilledParameters } = useTemplateValidation();

  // Auto-fetch invoice PDFs staged by callers (e.g. incasso DossierDetailSheet)
  useInvoiceAttachmentPrefetch();
  
  // Helper component for tab titles with alert dots
  const TabTitle = ({ title, showAlert }: { title: string; showAlert: boolean }) => (
    <div className="flex items-center gap-2">
      <span>{title}</span>
      {showAlert && (
        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
      )}
    </div>
  );

  // Calculate valid contacts for send functionality
  const validContacts = useMemo(() => 
    state.contacts.filter(contact => 
      contact.email?.trim() && contact.email.trim() !== 'GSM-nummer onbekend'
    ), [state.contacts]
  );

  // Geselecteerde ontvangers zonder bruikbaar adres — die verklaren een "Verzend (0)".
  const contactsWithoutEmail = useMemo(
    () =>
      state.contacts
        .filter((contact) => {
          const email = contact.email?.trim();
          return !email || email === 'GSM-nummer onbekend';
        })
        .map((contact) => contact.name || contact.firstName || 'Onbekend contact'),
    [state.contacts]
  );

  // Reden waarom er (nog) niet verzonden kan worden; null = alles in orde.
  const sendBlockReason = useMemo(
    () =>
      getSendBlockReason({
        subject: state.emailSubject,
        content: state.emailContent,
        validContactCount: validContacts.length,
        contactsWithoutEmail,
        isTemplateValid,
      }),
    [state.emailSubject, state.emailContent, validContacts.length, contactsWithoutEmail, isTemplateValid]
  );

  // Check if we can send the email
  const canSend = useMemo(() => {
    return sendBlockReason === null && !state.isSending;
  }, [sendBlockReason, state.isSending]);

  // Build an Outlook-style quoted block for display in the WYSIWYG editor.
  // Shows the full email body (including nested thread history) like Outlook/Gmail do.
  // The backend's stripQuotedReplyContent strips this when sending via Graph API to avoid double-quoting.
  const buildOutlookStyleQuote = (email: HelpdeskEmail): string => {
    const dateStr = new Date(email.receivedDateTime).toLocaleString('nl-NL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const toAddress = email.metadata?.to || 'helpdesk@yally.be';

    // Clean the body: strip CID image refs and Yally template wrapper, keep full thread history
    let cleanBody = stripCidReferences(email.body);
    cleanBody = stripYallyTemplateWrapper(cleanBody);

    return [
      '<br>',
      QUOTED_REPLY_MARKER,
      '<hr style="border: none; border-top: 1px solid #b5b5b5; margin: 16px 0;">',
      '<div style="font-size: 13px; color: #666;">',
      `  <p style="margin: 2px 0;"><strong>Van:</strong> ${email.from}</p>`,
      `  <p style="margin: 2px 0;"><strong>Verzonden:</strong> ${dateStr}</p>`,
      `  <p style="margin: 2px 0;"><strong>Aan:</strong> ${toAddress}</p>`,
      `  <p style="margin: 2px 0;"><strong>Onderwerp:</strong> ${email.subject}</p>`,
      '</div>',
      '<br>',
      `<div>${cleanBody}</div>`,
    ].join('\n');
  };

  // Handle reply to email selection
  const handleReplyToEmail = (email: HelpdeskEmail) => {
    // Toggle off if clicking the same email
    if (replyingToEmail?.id === email.id) {
      setReplyingToEmail(null);
      dispatch({ type: 'SET_EMAIL_CONTENT', payload: '' });
      return;
    }

    setReplyingToEmail(email);

    // Update subject with RE: prefix if not already present
    const originalSubject = email.subject.replace(/^(RE:|Re:|re:|AW:|Aw:|aw:)\s*/i, '').trim();
    if (!state.emailSubject.toLowerCase().startsWith('re:') && !state.emailSubject.toLowerCase().startsWith('aw:')) {
      dispatch({ type: 'SET_EMAIL_SUBJECT', payload: `RE: ${originalSubject}` });
    }

    // Build Outlook-style quoted content for the WYSIWYG editor.
    // Shows full thread history like Outlook/Gmail — the backend strips this for Graph API.
    const quotedContent = buildOutlookStyleQuote(email);
    dispatch({ type: 'SET_EMAIL_CONTENT', payload: quotedContent });
  };

  const handleSend = async () => {
    if (!canSend) return;

    const emailData = {
      subject: state.emailSubject,
      content: state.emailContent,
      senderEmail: state.senderEmail,
      contacts: validContacts,
      parameters: state.parameters,
      parameterValues: state.parameterValues,
      contextPlaceholders: state.contextPlaceholders,
      mentionedEntities: state.mentionedEntities,
      attachedFiles: state.attachedFiles, // Pass the full AttachedFile objects with File data
      emailSettings: state.emailSettings,
      replyToMessageId: replyingToEmail?.messageId || null,
      ccContacts: state.ccContacts,
      bccContacts: state.bccContacts
    };

    try {
      await sendMassEmail(emailData);
      // Clear the replying state after successful send
      setReplyingToEmail(null);

      // De aanroeper toont het rapport zelf: composer meteen sluiten.
      if (closeOnSuccess) {
        onSuccess?.();
        if (isModal) onModalClose?.();
      }
    } catch (error) {
      console.error('Failed to send mass email:', error);
    }
  };

  const handleCloseResultsModal = () => {
    dispatch({ type: 'SET_RESULTS_MODAL_OPEN', payload: false });
    dispatch({ type: 'SET_SEND_RESULTS', payload: null });
    // Clear the form data but keep loaded templates and settings
    dispatch({ type: 'RESET_FORM_DATA' });
    
    // If in modal mode, close the modal and call success callback
    if (isModal && onModalClose) {
      onModalClose();
    }
    if (onSuccess) {
      onSuccess();
    }
  };

  // Initialize data on mount (only once)
  const initializedRef = useRef(false);
  useEffect(() => {
    // Prevent re-initialization on window focus or state changes
    if (initializedRef.current) {
      console.log('⏭️ Skipping initialization - already initialized');
      return;
    }
    initializedRef.current = true;
    
    console.log('🚀 Initializing mass mail data...');
    const initializeData = async () => {
      try {
        // Always fetch backend templates and email settings
        // Context templates (if provided) will be merged with backend templates in TemplateManager
        console.log('📡 Fetching template types, templates, and email settings...');
        await Promise.all([
          fetchTemplateTypes(),
          fetchTemplates(),
          fetchEmailSettings()
        ]);
        console.log('✅ Mass mail data initialized successfully');
      } catch (error) {
        console.error('❌ Error initializing mass mail data:', error);
      }
    };
    
    initializeData();
  }, []); // Empty deps - run only once on mount

  // Auto-trigger reply when replyToEmail is provided (e.g., from "Reageer" button)
  const replyTriggeredRef = useRef(false);
  useEffect(() => {
    if (replyToEmail && !replyTriggeredRef.current) {
      replyTriggeredRef.current = true;
      handleReplyToEmail(replyToEmail);
    }
  }, [replyToEmail]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-3">
          {!isModal && (
            <button
              type="button"
              onClick={handleBack}
              aria-label="Terug"
              title="Terug"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              <ArrowLeft size={18} weight="bold" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Mass Mail</h1>
            <p className="text-sm text-gray-500">
              {isModal
                ? 'Verstuur een e-mail met templates en parameters'
                : 'Verstuur e-mails naar meerdere contacten met templates'
              }
            </p>
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-3">
          {/* Progress indicator */}
          {state.isSending && <SendingProgress />}

          {/* Waarom kan er niet verzonden worden — anders staat de knop grijs zonder uitleg */}
          {!state.isSending && sendBlockReason && (
            <div className="flex max-w-md items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
              <span className="text-xs leading-snug text-amber-900">{sendBlockReason}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="bg-moss-primary hover:bg-moss-primary/90 text-white"
              startContent={<PaperPlaneTilt size={16} />}
              onPress={handleSend}
              isDisabled={!canSend}
              isLoading={state.isSending}
            >
              {state.isSending
                ? 'Verzenden...'
                : `Verzend (${validContacts.length})`
              }
            </Button>
          </div>
        </div>
      </div>

      {/* Reply indicator banner */}
      {replyingToEmail && (
        <div className="bg-brick-primary/10 border-b border-brick-primary/20 px-6 py-3 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ArrowBendUpLeft size={18} className="text-brick-primary" weight="bold" />
              <div className="text-sm">
                <span className="text-neutral-secondary-mid">Beantwoorden op: </span>
                <span className="font-medium text-neutral-primary">{replyingToEmail.subject}</span>
                <span className="text-neutral-secondary-mid ml-2">
                  ({new Date(replyingToEmail.receivedDateTime).toLocaleDateString('nl-NL')})
                </span>
              </div>
            </div>
            <Button
              size="sm"
              variant="light"
              isIconOnly
              className="text-neutral-secondary-mid hover:text-brick-primary"
              onPress={() => setReplyingToEmail(null)}
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content - Two Column Layout with Tabs */}
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Left Column - Gegevens */}
          <div className="flex flex-col">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Gegevens</h2>
            
            <Tabs
              selectedKey={leftTab}
              onSelectionChange={(key) => setLeftTab(key as string)}
              variant="underlined"
              classNames={{
                base: "shadow-[inset_0_-1px_0_0_rgba(49,64,59,0.2)]",
                tabList: "p-0 gap-6",
                tab: "text-base px-0 py-0 h-fit",
                tabContent: "py-2 w-full text-neutral-secondary-mid group-data-[selected=true]:text-brick-primary",
                cursor: "w-full bg-brick-primary",
              }}
            >
              {emailHistory && emailHistory.length > 0 && (
                <Tab key="email-verloop" title={`Email verloop (${emailHistory.length})`}>
                  <div className="py-4">
                    <EmailHistorySection
                      emails={emailHistory}
                      onReplyToEmail={handleReplyToEmail}
                      replyingToEmail={replyingToEmail}
                    />
                  </div>
                </Tab>
              )}
              <Tab
                key="contactpersonen"
                title={<TabTitle title="Contactpersonen" showAlert={false} />}
              >
                <div className="space-y-6 py-4">
                  <ResourceSelector />
                  <ContactsList />
                </div>
              </Tab>

              <Tab
                key="parameters"
                title={<TabTitle title="Parameters" showAlert={getMissingEntityTypes.missingParameters.length > 0 || hasUnfilledParameters} />}
              >
                <div className="py-4">
                  <ParameterManager />
                </div>
              </Tab>

              <Tab
                key="entiteiten"
                title={<TabTitle title="Entiteiten" showAlert={getMissingEntityTypes.missingTypes.length > 0} />}
              >
                <div className="py-4">
                  <EntitiesManager />
                </div>
              </Tab>

              <Tab
                key="velden"
                title={<TabTitle title="Velden" showAlert={false} />}
              >
                <div className="py-4">
                  <DynamicFieldsManager />
                </div>
              </Tab>
            </Tabs>
          </div>

          {/* Right Column - Email */}
          <div className="flex flex-col">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Email</h2>
            <Tabs
              selectedKey={rightTab}
              onSelectionChange={(key) => setRightTab(key as string)}
              variant="underlined"
              // De editor blijft gemonteerd terwijl je bijlagen of het voorbeeld
              // bekijkt. Standaard gooit HeroUI het inactieve tabblad weg, en dan
              // verdwijnt een AI-antwoordvoorstel dat je nog niet overgenomen had
              // (dat staat in het tabblad, niet in de mailtekst). Bijlagen en
              // voorbeeld hebben geen eigen ophaalwerk, dus dit kost niets.
              destroyInactiveTabPanel={false}
              classNames={{
                base: "shadow-[inset_0_-1px_0_0_rgba(49,64,59,0.2)]",
                tabList: "p-0 gap-6",
                tab: "text-base px-0 py-0 h-fit",
                tabContent: "py-2 w-full text-neutral-secondary-mid group-data-[selected=true]:text-brick-primary",
                cursor: "w-full bg-brick-primary",
              }}
            >
              <Tab key="editor" title="Editor">
                <div className="space-y-6 py-4">
                  <TemplateManager />
                  <EmailComposer />
                </div>
              </Tab>

              <Tab key="bijlagen" title="Bijlagen">
                <div className="py-4">
                  <AttachmentManager />
                </div>
              </Tab>

              <Tab key="voorbeeld" title="Voorbeeld">
                <div className="py-4">
                  <EmailPreview />
                </div>
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Send Results Modal */}
      <SendResultsModal
        isOpen={state.isResultsModalOpen}
        onClose={handleCloseResultsModal}
        results={state.sendResults}
      />
    </div>
  );
};
